/**
 * Scrolling an element into view without disturbing what should stay put.
 *
 * `scrollIntoView` scrolls *every* ancestor that can scroll, `overflow: hidden`
 * ones included (a dialog's content box, say, which pushes its header out of
 * view), and the document too. `centerInScrollPort` moves one box and stops;
 * `centerInScrollPorts` moves each ancestor that genuinely scrolls (`auto` /
 * `scroll` / `overlay` with room to move) and never an `overflow: hidden` one.
 */

const isScrollPort = (candidate: HTMLElement): boolean => {
  const { overflowY } = window.getComputedStyle(candidate);
  return (
    (overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'overlay') &&
    candidate.scrollHeight > candidate.clientHeight
  );
};

/** Nearest ancestor that both allows scrolling and currently has room to. */
export const findScrollPort = (element: HTMLElement): HTMLElement | null => {
  let candidate = element.parentElement;
  while (candidate) {
    if (isScrollPort(candidate)) return candidate;
    candidate = candidate.parentElement;
  }
  return null;
};

/** Every ancestor that allows scrolling and has room to, innermost first. */
export const findScrollPorts = (element: HTMLElement): HTMLElement[] => {
  const ports: HTMLElement[] = [];
  let candidate = element.parentElement;
  while (candidate) {
    if (isScrollPort(candidate)) ports.push(candidate);
    candidate = candidate.parentElement;
  }
  return ports;
};

/**
 * Center `element` in its own scroll box, touching nothing outside it.
 * `topInset` is the height pinned over that box; centering against the whole
 * box would leave high targets hidden behind it.
 */
export const centerInScrollPort = (element: HTMLElement, topInset = 0) => {
  const port = findScrollPort(element);
  if (!port) return;
  const entry = element.getBoundingClientRect();
  const view = port.getBoundingClientRect();
  const usableTop = view.top + topInset;
  const usableHeight = Math.max(view.height - topInset, 0);
  // Too tall to center below the panel without its top going behind it.
  const delta =
    entry.height >= usableHeight
      ? entry.top - usableTop
      : entry.top + entry.height / 2 - (usableTop + usableHeight / 2);
  port.scrollTo({ top: port.scrollTop + delta, behavior: 'smooth' });
};

/**
 * Center `element` in its nearest scroll box, then in each further scrollable
 * ancestor (a scroll box can itself sit partly outside an outer one). Boxes
 * with `overflow: hidden` are skipped. Scrolling is smooth, so the element's
 * rect is advanced by the scroll each box will actually make (clamped to its
 * range) rather than re-read.
 */
export const centerInScrollPorts = (element: HTMLElement) => {
  let top = element.getBoundingClientRect().top;
  const height = element.getBoundingClientRect().height;
  for (const port of findScrollPorts(element)) {
    const view = port.getBoundingClientRect();
    const delta =
      height >= view.height
        ? top - view.top
        : top + height / 2 - (view.top + view.height / 2);
    const maxTop = port.scrollHeight - port.clientHeight;
    const next = Math.min(Math.max(port.scrollTop + delta, 0), maxTop);
    port.scrollTo({ top: next, behavior: 'smooth' });
    top -= next - port.scrollTop;
  }
};
